let saxonJsLoaded = false;
let saxonJsLoadPromise = null;

function loadSaxonJs() {
    if (saxonJsLoaded) return Promise.resolve();
    if (window.SaxonJS) {
        saxonJsLoaded = true;
        return Promise.resolve();
    }
    if (saxonJsLoadPromise) return saxonJsLoadPromise;
    saxonJsLoadPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = './_content/UP.Components/Lib/SaxonJS/SaxonJS2.js';
        script.onload = () => {
            saxonJsLoaded = true;
            resolve();
        };
        script.onerror = () => reject(new Error('Failed to load SaxonJS'));
        document.head.appendChild(script);
    });
    return saxonJsLoadPromise;
}

function byteArrayToString(byteArray) {
    return new TextDecoder().decode(
        byteArray instanceof Uint8Array ? byteArray : new Uint8Array(byteArray)
    );
}

let compiledSef = null;
let optimizedSef = null;

// SaxonJS does not support disable-output-escaping (DOE) with destination "serialized".
// S1000D XSLTs commonly use DOE to emit HTML entities which SaxonJS escapes as &amp;entity;.
// This map covers the most common HTML entities used in technical documentation.
const doeEntityMap = {
    '&amp;nbsp;': '\u00A0',     // non-breaking space
    '&amp;mdash;': '\u2014',    // em dash
    '&amp;ndash;': '\u2013',    // en dash
    '&amp;lsquo;': '\u2018',   // left single quote
    '&amp;rsquo;': '\u2019',   // right single quote
    '&amp;ldquo;': '\u201C',   // left double quote
    '&amp;rdquo;': '\u201D',   // right double quote
    '&amp;bull;': '\u2022',    // bullet
    '&amp;hellip;': '\u2026',  // horizontal ellipsis
    '&amp;trade;': '\u2122',   // trademark
    '&amp;copy;': '\u00A9',    // copyright
    '&amp;reg;': '\u00AE',     // registered
    '&amp;deg;': '\u00B0',     // degree
    '&amp;plusmn;': '\u00B1',  // plus-minus
    '&amp;micro;': '\u00B5',   // micro
    '&amp;middot;': '\u00B7',  // middle dot
    '&amp;frac12;': '\u00BD',  // fraction 1/2
    '&amp;frac14;': '\u00BC',  // fraction 1/4
    '&amp;frac34;': '\u00BE',  // fraction 3/4
    '&amp;times;': '\u00D7',   // multiplication
    '&amp;divide;': '\u00F7',  // division
    '&amp;le;': '\u2264',      // less than or equal
    '&amp;ge;': '\u2265',      // greater than or equal
};

const doePattern = new RegExp(Object.keys(doeEntityMap).join('|'), 'g');

function fixDisableOutputEscaping(html) {
    return html.replace(doePattern, match => doeEntityMap[match]);
}

export const preloadSaxonJs = function () {
    loadSaxonJs();
};

export const stylesheetNeedsCompilation = function () {
    return !compiledSef && !optimizedSef;
};

export const loadSefFromIndexedDb = async function (dbName, table, assetId) {
    await loadSaxonJs();

    const assembler = await import('./AssetAssemblerInterop.min.js');
    const content = await assembler.getAssetRecord(dbName, table, assetId);
    if (!content) {
        compiledSef = null;
        optimizedSef = null;
        return false;
    }

    try {
        const sefContent = new TextDecoder().decode(content);
        compiledSef = JSON.parse(sefContent);
        optimizedSef = null;
        return true;
    } catch (e) {
        console.error('SEF Parse Error:', e);
        compiledSef = null;
        optimizedSef = null;
        return false;
    }
};

export const transformXmlUsingXsl = async function (iframeElement, xml) {
    const xmlContent = byteArrayToString(xml);

    const activeSef = optimizedSef || compiledSef;
    if (!activeSef) {
        console.error('Stylesheet not compiled');
        return;
    }

    try {
        const result = await SaxonJS.transform({
            stylesheetInternal: activeSef,
            sourceText: xmlContent,
            destination: "serialized"
        }, "async");

        if (result.stylesheetInternal) {
            optimizedSef = result.stylesheetInternal;
        }

        if (result.principalResult && iframeElement) {
            // Release previous document memory before loading new content
            iframeElement.srcdoc = '';
            // SaxonJS does not support disable-output-escaping with destination "serialized".
            // S1000D XSLTs commonly use DOE to emit HTML entities, which SaxonJS instead
            // serializes as &amp;entity; (literal text). Replace with actual characters.
            iframeElement.srcdoc = fixDisableOutputEscaping(result.principalResult);
        }
    } catch (e) {
        console.error('Saxon-JS Transform Error:', e);
    }
};

const spinnerVariants = [
    // Circle (original)
    `<svg style="animation:spin 1s linear infinite;height:2.5rem;width:2.5rem;" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <circle style="opacity:0.25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path style="opacity:0.75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" fill="currentColor"></path>
    </svg>`,
    // Dots
    `<div style="display:flex;align-items:center;gap:6px;">
        <span style="width:10px;height:10px;border-radius:50%;background:currentColor;animation:bounce 1.4s ease-in-out infinite;animation-delay:0s"></span>
        <span style="width:10px;height:10px;border-radius:50%;background:currentColor;animation:bounce 1.4s ease-in-out infinite;animation-delay:0.16s"></span>
        <span style="width:10px;height:10px;border-radius:50%;background:currentColor;animation:bounce 1.4s ease-in-out infinite;animation-delay:0.32s"></span>
    </div>`,
    // Pulse Ring
    `<div style="position:relative;width:2.5rem;height:2.5rem;">
        <span style="position:absolute;inset:0;border-radius:50%;border:2px solid currentColor;animation:pulseRing 1.2s ease-out infinite"></span>
        <span style="position:absolute;inset:0;border-radius:50%;border:2px solid currentColor;animation:pulseRing 1.2s ease-out infinite;animation-delay:0.6s"></span>
    </div>`,
    // Bars
    `<div style="display:flex;align-items:center;justify-content:center;gap:2px;height:2.5rem;">
        <span style="display:inline-block;width:4px;height:2rem;background:currentColor;border-radius:2px;animation:bar 1.2s ease-in-out infinite;animation-delay:0s"></span>
        <span style="display:inline-block;width:4px;height:2rem;background:currentColor;border-radius:2px;animation:bar 1.2s ease-in-out infinite;animation-delay:0.12s"></span>
        <span style="display:inline-block;width:4px;height:2rem;background:currentColor;border-radius:2px;animation:bar 1.2s ease-in-out infinite;animation-delay:0.24s"></span>
        <span style="display:inline-block;width:4px;height:2rem;background:currentColor;border-radius:2px;animation:bar 1.2s ease-in-out infinite;animation-delay:0.36s"></span>
        <span style="display:inline-block;width:4px;height:2rem;background:currentColor;border-radius:2px;animation:bar 1.2s ease-in-out infinite;animation-delay:0.48s"></span>
    </div>`,
    // Orbital
    `<div style="position:relative;width:2.5rem;height:2.5rem;">
        <span style="position:absolute;inset:35%;border-radius:50%;background:currentColor;opacity:0.3"></span>
        <span style="position:absolute;top:50%;left:50%;width:8px;height:8px;margin:-4px 0 0 -4px;border-radius:50%;background:currentColor;animation:orbit 1.2s linear infinite"></span>
    </div>`
];

const themeColors = [
    {h: 193, s: 63, l: 45},  // primary
    {h: 343, s: 53, l: 42},  // secondary
    {h: 43, s: 47, l: 40},   // tertiary
    {h: 142, s: 52, l: 36},  // success
];

const themeColorsDark = [
    {h: 193, s: 63, l: 55},
    {h: 343, s: 53, l: 58},
    {h: 43, s: 47, l: 61},
    {h: 142, s: 52, l: 46},
];

export const clearHtml = function (iframeElement, isDarkMode) {
    if (iframeElement) {
        const colors = isDarkMode ? themeColorsDark : themeColors;
        const chosen = colors[Math.floor(Math.random() * colors.length)];
        const color = `hsl(${chosen.h}, ${chosen.s}%, ${chosen.l}%)`;
        const variant = spinnerVariants[Math.floor(Math.random() * spinnerVariants.length)];

        iframeElement.srcdoc = `<!DOCTYPE html>
        <html lang="en">
        <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>Loading</title>
            <style>
                body, html { margin: 0; padding: 0; height: 100%; }
                .container { display: flex; align-items: center; justify-content: center; height: 100vh; color: ${color}; }
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                @keyframes bounce { 0%, 80%, 100% { transform: scale(0); } 40% { transform: scale(1); } }
                @keyframes pulseRing { 0% { transform: scale(0.5); opacity: 0.8; } 100% { transform: scale(1.8); opacity: 0; } }
                @keyframes bar { 0%, 40%, 100% { transform: scaleY(0.4); } 20% { transform: scaleY(1); } }
                @keyframes orbit { 0% { transform: rotate(0deg) translateX(150%) rotate(0deg); } 100% { transform: rotate(360deg) translateX(150%) rotate(-360deg); } }
            </style>
        </head>
        <body>
            <div class="container">${variant}</div>
        </body>
        </html>`;
    }
};

export const clearStylesheet = function () {
    compiledSef = null;
    optimizedSef = null;
};
